/**
 * Where a stretch kit's halves meet. A kit frame is one mesh whose n half and p
 * half slide towards each other under the In morphs, with a stretching seam
 * between them. Squeezed past the point where the halves touch, they cross: the
 * corners overlap and the frame folds. Pure and Babylon-free, so the engine and
 * the offline measurement (tools/wrappers/measure.ts) read the seam the same way.
 */

/** Vertices this close to the pivot plane (metres) are the seam row: in neither half. Exports leave a few a hair off the plane. */
export const SEAM_ROW_M = 1e-3;

/**
 * The least the halves are left apart (metres). Touching exactly collapses the stretching strip to zero length, which the
 * triangles across it cannot draw cleanly, so the minimum stops a hair short.
 */
export const SEAM_GAP_M = 1e-3;

/** The most influence a stretch morph is driven to: MorphStretch's clamp, and how far the seam is searched. */
export const MAX_MORPH_INFLUENCE = 10;

/**
 * The In influence at which the halves of a mesh meet along one axis (come
 * within {@link SEAM_GAP_M} of each other), or Infinity when they never do
 * within `limit`.
 *
 * `rest[i]` is vertex i's position along the axis and `shift[i]` how far the
 * axis's In morphs move it per unit of influence, both in the object's meters.
 * Vertices below the seam row are the n half, above it the p half. Infinity
 * also stands for "no seam to measure": a half is missing.
 */
export function halvesMeetAt(rest: ArrayLike<number>, shift: ArrayLike<number>, limit: number): number {
    const n: number[] = [], p: number[] = [];
    for (let i = 0; i < rest.length; i++) {
        if (rest[i] < -SEAM_ROW_M) n.push(i);
        else if (rest[i] > SEAM_ROW_M) p.push(i);
    }
    if (!n.length || !p.length) return Infinity;
    // The p half's nearest vertex minus the n half's furthest: a concave
    // function of influence, so it falls through any gap once.
    const gap = (influence: number): number => {
        let nearest = Infinity, furthest = -Infinity;
        for (const i of p) nearest = Math.min(nearest, rest[i] + influence * shift[i]);
        for (const i of n) furthest = Math.max(furthest, rest[i] + influence * shift[i]);
        return nearest - furthest;
    };
    // The halves start apart (the seam row separates them), so it is enough that they are not apart at `limit`.
    if (!(gap(limit) <= SEAM_GAP_M)) return Infinity;
    let apart = 0, closed = limit;
    for (let step = 0; step < 48; step++) {
        const mid = (apart + closed) / 2;
        if (gap(mid) > SEAM_GAP_M) apart = mid; else closed = mid;
    }
    return apart;
}
